import { CheckOutlined } from '@ant-design/icons';
import { Button, Checkbox, message, Modal, Spin } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { RadioChangeEvent } from 'antd/lib/radio';
import moment from 'moment';
import React, { Fragment, useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import {
  MethodWay,
  openEULAPdf,
  openPrivatePolicy,
} from '../../../../generic/common';
import { DT0Format } from '../../../../generic/StaticVariable';
import { StoreContext } from '../../../../store/store';
import { ICountry, IState, IUserProfile } from '../../../../typings/api';
import { PriceSummaryResult } from '../../../../typings/checkout';
import styles from '../style/Checkout.module.scss';
import { useUserProvider } from '../../../../generic/hooks';

export default function Summary(props: {
  method: MethodWay;
  userDetails: IUserProfile | undefined;
}) {
  const {
    user,
    setShoppingItems,
    shoppingItems,
    setMarkerCollections,
    setShowResultCollapse,
    setPreviewAnalytics,
    countryList,
  } = useContext(StoreContext);

  const [showAssociatedCompanyWarning] = useUserProvider()
  const [checkPolicy, setCheckPolicy] = useState<boolean>(false);
  const [showLocation, setShowLocation] = useState<boolean>(true);
  const [showWarn, setShowWarn] = useState<boolean>(false);
  const [checkoutDisabled, setCheckoutDisabled] = useState<boolean>(true);
  const [createOrderError, setCreateOrderError] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [freeOrderId, setFreeOrderId] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [redirectingToPaymentProvider, setRedirectingToPaymentProvider] =
    useState(false);

  const { userDetails } = props;
  const [downloadValues, setDownloadValues] = useState<CheckboxValueType[]>([
    'csv',
  ]);
  const [priceSummary, setPriceSummary] = useState<PriceSummaryResult>(
    {} as any
  );
  const [loadingPrice, setLoadingPrice] = useState(false);

  const downloadOptions = [
    { label: '.csv', value: 'csv' },
    { label: '.mat', value: 'mat' },
    { label: '.nc', value: 'nc' },
    { label: '.dfs0', value: 'dfs0' },
  ];

  const emptyPrice = isEmpty(priceSummary);

  useEffect(() => {
    const isLoggedIn = !isEmpty(user);
    if (!isLoggedIn) {
      setPriceSummary({} as PriceSummaryResult);
    } else {
      userDetails !== undefined && !isEmpty(userDetails) && getTotalPrice();
    }
  }, [user, userDetails]);

  useEffect(() => {
    const enableCheckout =
      checkPolicy && userDetails !== undefined && !emptyPrice;
    let address = '';
    if (userDetails !== undefined && !isEmpty(userDetails)) {
      const usersCountry = countryList.find(
        (country: ICountry) => country.id === userDetails.countryId
      );
      if (usersCountry !== undefined) {
        let cityState = userDetails.city;
        if (userDetails.stateId && !isEmpty(usersCountry.states)) {
          const usersState = usersCountry.states.find(
            (state: IState) => state.id === userDetails.stateId
          );
          if (usersState !== undefined) {
            cityState += ',' + usersState.name;
          }
        }
        address = userDetails.address1;
        userDetails.address2 && userDetails.address2.trim()
          ? (address += '||' + userDetails.address2)
          : (address += '||null');
        userDetails.address3 && userDetails.address3.trim()
          ? (address += '||' + userDetails.address3)
          : (address += '||null');
        address +=
          '||' +
          cityState +
          '||' +
          userDetails.zipcode +
          '||' +
          usersCountry.name;
      }
    }
    setBillingAddress(address);
    if (isEmpty(user) || isEmpty(shoppingItems)) {
      setCheckoutDisabled(true);
    } else {
      setCheckoutDisabled(!enableCheckout);
    }
  }, [userDetails, checkPolicy, user, shoppingItems, countryList, emptyPrice]);

  async function getTotalPrice() {
    setLoadingPrice(true);
    try {
      const data = await Api.price.GetPriceSummary();
      if (data) {
        if (data.Error) {
          const errorMessage = data.Error.ErrorMessage
            ? data.Error.ErrorMessage
            : 'Failed to get total price';
          message.error(errorMessage);
          setPriceSummary({} as PriceSummaryResult);
        } else {
          setPriceSummary(data.Result ? data.Result : {});
        }
      }
    } catch {
      setPriceSummary({} as PriceSummaryResult);
    } finally {
      setLoadingPrice(false);
    }
  }

  function onPolicyRadioChange(e: RadioChangeEvent) {
    setCheckPolicy(e.target.checked);
    setShowWarn(false);
  }

  function onLocationRadioChange(e: RadioChangeEvent) {
    setShowLocation(e.target.checked);
  }

  async function onClickCheckout() {
    if (!checkPolicy) {
      setShowWarn(true);
    } else {
      await onCheckout();
    }
  }

  function onCloseCreateOrderFailedDialog() {
    setCreateOrderError('');
  }

  function onCloseGoToPaymentDialog() {
    setPaymentUrl('');
  }

  function clearStore() {
    setMarkerCollections([]);
    setShowResultCollapse(false);
    setPreviewAnalytics([]);
  }

  async function onCloseFreeCheckoutSucceededDialog() {
    setFreeOrderId('');
    clearStore();
    const data = await Api.shopping.GetShoppingCart(true);
    setShoppingItems(data.shoppingItems);
  }

  async function createOrder() {
    setCreatingOrder(true);
    const data = await Api.shopping.GetShoppingCart(true);
    const details: string[] = [];    
    const orderIds = new Array<string>();
    Object.keys(data.shoppingItems).forEach((pointName: string) => {
      const itemsPerPoint = data.shoppingItems[pointName];
      Object.keys(itemsPerPoint).forEach((category: string) => {
        const item = itemsPerPoint[category];
        orderIds.push(item.order_id);
        const startTime = moment(item.start_date);
        const endTime = moment(item.end_date);        
        details.push(
          item.dataset_id +
          '&' +
          startTime.format(DT0Format) +
          '&' +
          endTime.format(DT0Format) +
          '&' +
          item.dataset_category +
          '&' +
          item.location +
          '&' +
          'M' +
          '&' +
          '.csv' +
          '&' +
          '0' +
          '&' +
          item.price +
          '&'
        );
      });
    });
   

    const detailsPayload = details.join('|');
    if (!billingAddress && !showAssociatedCompanyWarning) {
      setCreateOrderError(
        'Your profile is not complete. Please go to your account and update the information.'
      );
      return
    }

    const result = await Api.moodV2Order.createOrder(
      detailsPayload,
      downloadValues.length > 0 ? downloadValues.join('|') : 'csv',
      orderIds,
      billingAddress,
      showLocation
    );
    if (result.Error) {
      setCreateOrderError(
        result.Error.ErrorMessage
          ? result.Error.ErrorMessage
          : 'We are sorry, we could not create your order.'
      );
    } else if (result && result.Result) {
      if (result.Result.paymentUrl) {
        setPaymentUrl(result.Result.paymentUrl);
      } else if (result.Result.orderId) {
        setFreeOrderId(result.Result.orderId);
      }
    }

    setCreatingOrder(false);
    const shopingCardData = await Api.shopping.GetShoppingCart();
    setShoppingItems(shopingCardData.shoppingItems);
  }

  const onCheckout = async () => {
    await createOrder();
  };

  function onChangeDownloadOptions(checkedList: CheckboxValueType[]) {
    setDownloadValues(checkedList);
  }

  function onRedirectToPaymentGateway() {
    setRedirectingToPaymentProvider(true);
  }

  const currency = priceSummary.CurrencySymbol;

  return (
    <div className={styles.summaryRoot}>
      <Modal
        closable={false}
        keyboard={false}
        visible={freeOrderId !== ''}
        title={'Checkout successful'}
        footer={
          <Link to={'/'}>
            <Button onClick={onCloseFreeCheckoutSucceededDialog}>OK</Button>
          </Link>
        }
      >
        {
          'An email with a link to download the data will be sent to you within a few minutes. You will be redirected to the main page.'
        }
      </Modal>
      <Modal
        visible={createOrderError !== ''}
        title={'Error during order processing'}
        footer={<Button onClick={onCloseCreateOrderFailedDialog}>OK</Button>}
      >
        <p>{createOrderError}</p>
      </Modal>
      <Modal
        onCancel={onCloseGoToPaymentDialog}
        closable={!redirectingToPaymentProvider}
        keyboard={!redirectingToPaymentProvider}
        visible={paymentUrl !== ''}
        title={'Your order has been created'}
        footer={null}
      >
        <div className={styles.goToPaymentContainer}>
          <div className={styles.space} />
          <Button
            onClick={onRedirectToPaymentGateway}
            type={'link'}
            href={paymentUrl}
          >
            <div className={styles.goToPaymentStyle}>
              Go to payment provider
            </div>
          </Button>
          <div className={styles.space} />
          {redirectingToPaymentProvider && (
            <div className={styles.spinner}>
              <Spin size="large" />
              <div className={styles.space} />
            </div>
          )}
        </div>
      </Modal>
      {loadingPrice ? (
        <div className={styles.spinner}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <div className={styles.summaryContent}>
            <p className={styles.summaryInfo}>
              <span>
                Price: {emptyPrice ? '' : currency + priceSummary.Price}{' '}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Multipoint-discount:{' '}
                {emptyPrice ? '' : currency + priceSummary.MultipointDiscount}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Credit Deducted:{' '}
                {emptyPrice ? '' : currency + priceSummary.CreditDeducted}
              </span>
            </p>
            {props.method === MethodWay.INV && (
              <p className={styles.summaryInfo}>
                <span>Invoice Fee</span>
              </p>
            )}
          </div>
          <div className={styles.summaryContent}>
            <p className={styles.summaryInfo}>
              <span>
                Subtotal: {emptyPrice ? '' : currency + priceSummary.Subtotal}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Estimated Tax{' '}
                {emptyPrice
                  ? ':'
                  : ' (VAT): ' + currency + priceSummary.EstimatedVAT}
              </span>
            </p>
          </div>
          <div className={styles.summaryContent} style={{ border: 'none' }}>
            <p className={styles.summaryInfo}>
              <span>
                Total: {emptyPrice ? '' : currency + priceSummary.Total}
              </span>
            </p>
          </div>

          <div className={styles.outputType}>
            <p>Select file format(s) of time series data:</p>
            <Checkbox.Group
              options={downloadOptions}
              defaultValue={downloadValues}
              onChange={onChangeDownloadOptions}
            />
          </div>
        </div>
      )}

      <div>
        <div>
          <Link to={'/app'}>
            <Button type="primary" className={styles.checkoutButton}>
              {'Go back to main page'}
            </Button>
          </Link>

          <Button
            loading={creatingOrder}
            disabled={checkoutDisabled}
            type="primary"
            icon={<CheckOutlined />}
            className={
              styles.checkoutButton +
              ` ${checkoutDisabled && styles.checkoutButtonDisabled}`
            }
            onClick={onClickCheckout}
          >
            Checkout
          </Button>
        </div>

        <div>
          <p
            className={
              styles.summaryInfo + ` ${showWarn && styles.summaryWarn}`
            }
          >
            <Checkbox
              defaultChecked={checkPolicy}
              onChange={onPolicyRadioChange}
            >
              <span>
                I accept the{' '}
                <span
                  className={styles.summarySpecialText}
                  onClick={openEULAPdf}
                >
                  General EULA
                </span>{' '}
                and{' '}
                <span
                  className={styles.summarySpecialText}
                  onClick={openPrivatePolicy}
                >
                  Privacy Policy
                </span>
              </span>
              {showWarn && (
                <Fragment>
                  <br />
                  <span className={styles.summaryWarnText}>
                    Please tick checkbox before checkout.
                  </span>
                </Fragment>
              )}
            </Checkbox>
          </p>
          <p className={styles.summaryInfo}>
            <Checkbox
              defaultChecked={showLocation}
              onChange={onLocationRadioChange}
            >
              Show location coordinates on invoice
            </Checkbox>
          </p>
        </div>
      </div>
    </div>
  );
}
